import React, { useCallback, useEffect, useState } from "react";
import { mixingAPI, productsAPI } from "../services/api";
import { useI18n } from "../i18n";
import useShopCurrency from "../hooks/useShopCurrency";
import { formatMoney } from "../pos/utils/money";
import { formatDate } from "../utils/dates";
import { trimmedDecimal } from "../utils/numberFormat";
import Modal from "../components/Modal";
import { useMembership } from "../context/MembershipContext";
import EmptyState from "../components/EmptyState";

/**
 * Recipes, and mixing a batch from one.
 *
 * Matches how the shops actually mix: the base is bought ready-made (a 50/50
 * or a 70/30 bottle), each flavour is dosed as a percentage of that base — the
 * way flavour makers print their own instructions — and a nicotine booster is
 * optional, since a flavour often carries some nicotine of its own.
 *
 * The batch panel calls the same planning endpoint the real thing uses, so
 * what the owner sees before he commits is exactly what will happen — a
 * preview computed separately would eventually disagree with reality, and he
 * would be the one holding a ruined jug.
 */
// Only used before the shop has tagged any packaging with a bottle size —
// see bottleOptions below, which is what actually drives the picker
const FALLBACK_BOTTLE_SIZES = [10, 30, 50, 60, 100];

const BLANK_RECIPE = {
  name: "",
  product_id: "",
  base_material_id: "",
  nicotine_material_id: "",
  target_nicotine_mg: "",
  steep_days: 5,
  lines: [],
};

const Recipes = () => {
  const { t } = useI18n();
  const { can } = useMembership();
  const currency = useShopCurrency();
  const money = (value) => formatMoney(value, currency);
  const unitLabel = (unit) =>
    unit === "ml" || unit === "pcs" ? t(`mixing.materials.units.${unit}`) : unit;

  const [recipes, setRecipes] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  // Batch panel — the recipe fixes what goes in; this only asks how much base
  const [mixing, setMixing] = useState(null);
  const [baseMl, setBaseMl] = useState("250");
  const [bottleSize, setBottleSize] = useState(30);
  const [plan, setPlan] = useState(null);
  const [planError, setPlanError] = useState("");

  const load = useCallback(async () => {
    try {
      const [r, m, p] = await Promise.all([
        mixingAPI.recipes(),
        mixingAPI.materials(),
        productsAPI.getAll(),
      ]);
      setRecipes(r.data);
      setMaterials(m.data.materials);
      setProducts(p.data);
      setError("");
    } catch (err) {
      setError(err.response?.data?.error || t("mixing.recipes.loadFailed"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const byType = (type) => materials.filter((m) => m.type === type);

  // The sizes the shop actually stocks packaging for — not a fixed list.
  // Sorted ascending so the picker reads smallest to largest.
  const bottleOptions = [
    ...new Set(
      byType("packaging")
        .map((m) => Number(m.size_ml))
        .filter((size) => Number.isInteger(size) && size > 0),
    ),
  ].sort((a, b) => a - b);

  // Packaging tied to the chosen size (a bottle) plus anything that applies
  // to every size regardless (a label, say). The size picker below only
  // offers sizes with packaging behind them, so this always finds one in
  // normal use — it used to silently cost and consume whatever packaging
  // happened to sort first instead, regardless of the size actually chosen.
  const packagingLines = byType("packaging")
    .filter((m) => !m.size_ml || Number(m.size_ml) === Number(bottleSize))
    .map((m) => ({ material_id: m.id, per_bottle: 1 }));

  // Re-cost whenever the recipe, the base amount or the bottle size changes
  useEffect(() => {
    if (!mixing) return;

    let cancelled = false;

    const run = async () => {
      try {
        const { data } = await mixingAPI.planBatch(mixing.id, {
          base_ml: parseFloat(baseMl),
          bottle_size_ml: bottleSize,
          packaging: packagingLines,
        });
        if (!cancelled) {
          setPlan(data);
          setPlanError("");
        }
      } catch (err) {
        if (!cancelled) {
          setPlan(null);
          setPlanError(
            err.response?.data?.message || t("mixing.recipes.planFailed"),
          );
        }
      }
    };

    const timer = setTimeout(run, 250);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mixing, baseMl, bottleSize, materials]);

  const saveRecipe = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError("");

    const payload = {
      ...editing,
      product_id: editing.product_id || null,
      base_material_id: editing.base_material_id || null,
      nicotine_material_id: editing.nicotine_material_id || null,
      target_nicotine_mg: editing.nicotine_material_id
        ? parseFloat(editing.target_nicotine_mg) || 0
        : 0,
      steep_days: parseInt(editing.steep_days, 10) || 0,
      lines: editing.lines.filter(
        (line) => line.material_id && line.percentage,
      ),
    };

    try {
      if (editing.id) {
        await mixingAPI.updateRecipe(editing.id, payload);
      } else {
        await mixingAPI.createRecipe(payload);
      }
      setEditing(null);
      setNotice(t("mixing.recipes.saved"));
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("mixing.recipes.saveFailed"));
    } finally {
      setBusy(false);
    }
  };

  const produce = async () => {
    setBusy(true);
    setError("");

    try {
      const { data } = await mixingAPI.produce(mixing.id, {
        base_ml: parseFloat(baseMl),
        bottle_size_ml: bottleSize,
        packaging: packagingLines,
      });
      // The browser knows how this owner reads dates; the server does not
      setNotice(
        data.batch?.status === "steeping"
          ? t("mixing.recipes.mixedReady", {
              day: formatDate(data.batch.ready_on),
            })
          : data.message,
      );
      setMixing(null);
      setPlan(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("mixing.recipes.mixFailed"));
    } finally {
      setBusy(false);
    }
  };

  const deleteRecipe = async (recipe) => {
    if (!window.confirm(t("mixing.recipes.deleteConfirm"))) return;

    try {
      const { data } = await mixingAPI.deleteRecipe(recipe.id);
      setNotice(data.message);
      setError("");
      await load();
    } catch (err) {
      setError(err.response?.data?.message || t("mixing.recipes.deleteFailed"));
    }
  };

  const setLine = (index, changes) =>
    setEditing((current) => ({
      ...current,
      lines: current.lines.map((line, i) =>
        i === index ? { ...line, ...changes } : line,
      ),
    }));

  /** Picking a flavour offers its maker's dose, so the owner rarely has to type one */
  const pickFlavour = (index, materialId) => {
    const material = materials.find((m) => m.id === materialId);
    const current = editing.lines[index];

    setLine(index, {
      material_id: materialId,
      percentage:
        current.percentage ||
        (material?.recommended_percentage
          ? trimmedDecimal(material.recommended_percentage, 1)
          : ""),
    });
  };

  const recipeBase = (recipe) =>
    materials.find((m) => m.id === recipe.base_material_id);

  const recipeSummary = (recipe) => {
    const base = recipe.base_name
      ? `${recipe.base_name} (${recipe.base_pg_ratio}/${recipe.base_vg_ratio})`
      : t("mixing.recipes.noBaseChosen");

    return recipe.nicotine_name && Number(recipe.target_nicotine_mg) > 0
      ? t("mixing.recipes.summaryBoosted", {
          base,
          nicotine: recipe.target_nicotine_mg,
          steep: recipe.steep_days,
        })
      : t("mixing.recipes.summary", { base, steep: recipe.steep_days });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-lg p-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            {t("mixing.recipes.title")}
          </h1>
          <p className="text-gray-600">{t("mixing.recipes.subtitle")}</p>
        </div>
        {can("mixing.edit") && (
          <button
            onClick={() =>
              setEditing(editing ? null : { ...BLANK_RECIPE, lines: [] })
            }
            className="px-5 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold"
          >
            {editing ? t("common.cancel") : t("mixing.recipes.new")}
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}

      {notice && !error && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {notice}
        </div>
      )}

      {editing && (
        <Modal
          title={
            editing.id
              ? t("mixing.recipes.editTitle")
              : t("mixing.recipes.addTitle")
          }
          onClose={() => setEditing(null)}
        >
        <form onSubmit={saveRecipe} className="space-y-5">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.name")}
              </span>
              <input
                type="text"
                required
                value={editing.name}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.fillsProduct")}
              </span>
              <select
                value={editing.product_id || ""}
                onChange={(e) =>
                  setEditing({ ...editing, product_id: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">{t("mixing.recipes.notLinked")}</option>
                {products
                  .filter((product) => product.category === "juice")
                  .map((product) => (
                    <option key={product.id} value={product.id}>
                      {product.name}
                    </option>
                  ))}
              </select>
            </label>

            <label className="block">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.steep")}
              </span>
              <input
                type="number"
                min="0"
                value={editing.steep_days}
                onChange={(e) =>
                  setEditing({ ...editing, steep_days: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-xs text-gray-500">
                {t("mixing.recipes.steepHint")}
              </span>
            </label>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block min-w-0">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.base")}
              </span>
              <select
                required
                value={editing.base_material_id || ""}
                onChange={(e) =>
                  setEditing({ ...editing, base_material_id: e.target.value })
                }
                className="mt-1 w-full px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="">{t("mixing.recipes.pickBase")}</option>
                {byType("base").map((material) => (
                  <option key={material.id} value={material.id}>
                    {material.name}
                    {material.pg_ratio !== null &&
                      ` (${material.pg_ratio}/${material.vg_ratio})`}
                  </option>
                ))}
              </select>
              <span className="text-xs text-gray-500">
                {t("mixing.recipes.baseHint")}
              </span>
            </label>

            <div className="block min-w-0">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.boosterOptional")}
              </span>
              <div className="mt-1 flex gap-2">
                <select
                  value={editing.nicotine_material_id || ""}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      nicotine_material_id: e.target.value,
                      target_nicotine_mg: e.target.value
                        ? editing.target_nicotine_mg
                        : "",
                    })
                  }
                  className="flex-1 min-w-0 px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">{t("mixing.recipes.noBooster")}</option>
                  {byType("nicotine").map((material) => (
                    <option key={material.id} value={material.id}>
                      {material.name}
                    </option>
                  ))}
                </select>
                {editing.nicotine_material_id && (
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    dir="ltr"
                    placeholder={t("mixing.recipes.targetNicotine")}
                    value={editing.target_nicotine_mg}
                    onChange={(e) =>
                      setEditing({
                        ...editing,
                        target_nicotine_mg: e.target.value,
                      })
                    }
                    className="w-28 px-3 py-2 border border-gray-300 rounded-lg"
                  />
                )}
              </div>
              <span className="text-xs text-gray-500">
                {editing.nicotine_material_id
                  ? t("mixing.recipes.targetNicotineHint")
                  : t("mixing.recipes.noBoosterHint")}
              </span>
            </div>
          </div>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <span className="text-sm font-medium text-gray-700">
                {t("mixing.recipes.flavours")}
              </span>
              <button
                type="button"
                onClick={() =>
                  setEditing({
                    ...editing,
                    lines: [
                      ...editing.lines,
                      { material_id: "", percentage: "" },
                    ],
                  })
                }
                className="text-sm text-primary-600 hover:underline"
              >
                {t("mixing.recipes.addFlavour")}
              </button>
            </div>

            {editing.lines.map((line, index) => (
              <div key={index} className="flex items-center gap-2 mb-2">
                <select
                  value={line.material_id}
                  onChange={(e) => pickFlavour(index, e.target.value)}
                  className="flex-1 min-w-0 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">{t("mixing.recipes.pickFlavour")}</option>
                  {[...byType("flavor"), ...byType("additive")].map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.recommended_percentage &&
                        ` — ${trimmedDecimal(m.recommended_percentage, 1)}%`}
                    </option>
                  ))}
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  dir="ltr"
                  placeholder="%"
                  value={line.percentage}
                  onChange={(e) =>
                    setLine(index, { percentage: e.target.value })
                  }
                  className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-right"
                />
                <button
                  type="button"
                  aria-label={t("common.remove")}
                  onClick={() =>
                    setEditing({
                      ...editing,
                      lines: editing.lines.filter((_, i) => i !== index),
                    })
                  }
                  className="text-gray-400 hover:text-red-600 px-2"
                >
                  ✕
                </button>
              </div>
            ))}
            <p className="text-xs text-gray-500">
              {t("mixing.recipes.doseHint")}
            </p>
          </div>

          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-60"
          >
            {busy ? t("common.saving") : t("mixing.recipes.save")}
          </button>
        </form>
        </Modal>
      )}

      <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">
            {t("mixing.recipes.yours")}
          </h2>
          {recipes.length === 0 ? (
            <EmptyState screen="recipes" message={t("mixing.recipes.noneYet")} />
          ) : (
            <ul className="divide-y divide-gray-100">
              {recipes.map((recipe) => (
                <li key={recipe.id} className="py-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">{recipe.name}</p>
                      <p className="text-sm text-gray-500">
                        {recipeSummary(recipe)}
                        {recipe.product_name && ` · ${recipe.product_name}`}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {can("mixing.edit") && (
                        <>
                          <button
                            onClick={() => {
                              setMixing(recipe);
                              setPlan(null);
                              setBottleSize(
                                bottleOptions[0] || FALLBACK_BOTTLE_SIZES[0],
                              );
                            }}
                            disabled={!recipeBase(recipe)}
                            title={
                              !recipeBase(recipe)
                                ? t("mixing.recipes.noBaseChosen")
                                : undefined
                            }
                            className="px-3 py-1.5 rounded-lg bg-primary-600 text-white text-sm font-semibold disabled:opacity-50"
                          >
                            {t("mixing.recipes.mix")}
                          </button>
                          <button
                            onClick={() =>
                              setEditing({
                                ...recipe,
                                target_nicotine_mg:
                                  Number(recipe.target_nicotine_mg) > 0
                                    ? recipe.target_nicotine_mg
                                    : "",
                                lines: recipe.lines.map((line) => ({
                                  material_id: line.material_id,
                                  percentage: trimmedDecimal(line.percentage, 1),
                                })),
                              })
                            }
                            className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm"
                          >
                            {t("common.edit")}
                          </button>
                        </>
                      )}
                      {can("mixing.delete") && (
                        <button
                          onClick={() => deleteRecipe(recipe)}
                          className="px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-sm hover:bg-red-50"
                        >
                          {t("common.remove")}
                        </button>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {mixing && (
          <Modal
            title={t("mixing.recipes.mixing", { name: mixing.name })}
            onClose={() => {
              setMixing(null);
              setPlan(null);
            }}
            maxWidthClass="max-w-2xl"
          >
            <p className="text-sm text-gray-500 -mt-2 mb-4">
              {t("mixing.recipes.howMuchBase")}
            </p>

            <div className="flex flex-wrap gap-3 mb-4">
              <label className="block">
                <span className="text-xs text-gray-500">
                  {t("mixing.recipes.batchSize")}
                </span>
                <input
                  type="number"
                  min="1"
                  value={baseMl}
                  onChange={(e) => setBaseMl(e.target.value)}
                  className="block mt-1 w-32 px-3 py-2 border border-gray-300 rounded-lg"
                />
              </label>
              <label className="block">
                <span className="text-xs text-gray-500">
                  {t("mixing.recipes.intoBottles")}
                </span>
                <select
                  value={bottleSize}
                  onChange={(e) => setBottleSize(Number(e.target.value))}
                  className="block mt-1 px-3 py-2 border border-gray-300 rounded-lg"
                >
                  {(bottleOptions.length > 0
                    ? bottleOptions
                    : FALLBACK_BOTTLE_SIZES
                  ).map((size) => (
                    <option key={size} value={size}>
                      {size} {unitLabel("ml")}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {bottleOptions.length === 0 && (
              <p className="text-xs text-amber-700 mb-4">
                {t("mixing.recipes.noBottlesTagged")}
              </p>
            )}

            {planError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 mb-3">
                {planError}
              </div>
            )}

            {plan && (
              <>
                <table className="w-full text-sm mb-4">
                  <tbody>
                    {plan.consumed.map((item) => (
                      <tr
                        key={item.material_id}
                        className="border-b border-gray-50"
                      >
                        <td className="py-1.5">{item.name}</td>
                        <td className="py-1.5 text-end text-gray-600 whitespace-nowrap">
                          {item.quantity} {unitLabel(item.unit)}
                        </td>
                        <td className="py-1.5 text-end w-24 whitespace-nowrap">
                          {money(item.subtotal)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="rounded-lg bg-gray-50 border border-gray-100 p-4 space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-600">
                      {t("mixing.recipes.finished")}
                    </span>
                    <span className="font-medium text-gray-800 whitespace-nowrap" dir="ltr">
                      {t("mixing.recipes.finishedDetail", {
                        volume: plan.volume_ml,
                        pg: plan.actual_pg_ratio,
                        vg: 100 - plan.actual_pg_ratio,
                        nicotine: plan.nicotine_mg,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-600">
                      {t("mixing.recipes.batchCost")}
                    </span>
                    <span className="font-semibold whitespace-nowrap">
                      {money(plan.total_cost)}
                    </span>
                  </div>
                  <div className="flex justify-between gap-3">
                    <span className="text-gray-600">
                      {t("mixing.recipes.bottles", {
                        count: plan.bottles,
                        size: plan.bottle_size_ml,
                      })}
                    </span>
                    <span className="font-bold text-emerald-700 whitespace-nowrap">
                      {t("mixing.recipes.each", {
                        amount: money(plan.computed_unit_cost),
                      })}
                    </span>
                  </div>
                  {plan.remainder_ml > 0 && (
                    <p className="text-xs text-gray-500 pt-1">
                      {t("mixing.recipes.leftover", {
                        ml: plan.remainder_ml,
                        count: plan.bottles,
                      })}
                    </p>
                  )}
                  {plan.recipe.steep_days > 0 && (
                    <p className="text-xs text-gray-500">
                      {t("mixing.recipes.readyIn", {
                        count: plan.recipe.steep_days,
                      })}
                    </p>
                  )}
                </div>

                {plan.shortages.length > 0 && (
                  <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    <p className="font-medium">{t("mixing.recipes.notEnough")}</p>
                    <ul className="mt-1 space-y-0.5">
                      {plan.shortages.map((short) => (
                        <li key={short.material_id}>
                          {t("mixing.recipes.shortBy", {
                            name: short.name,
                            amount: short.short_by,
                            unit: unitLabel(short.unit),
                          })}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <button
                  onClick={produce}
                  disabled={!plan.can_produce || busy || plan.bottles < 1}
                  className="mt-4 w-full px-6 py-3 rounded-lg bg-primary-600 hover:bg-primary-700 text-white font-semibold disabled:opacity-50"
                >
                  {busy
                    ? t("mixing.recipes.busy")
                    : t("mixing.recipes.mixBottles", { count: plan.bottles })}
                </button>
              </>
            )}
          </Modal>
        )}
    </div>
  );
};

export default Recipes;
